<template>
	<div class="container-fluid p-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory"
				title="Back to stock on hand"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div>
				<h1 class="mb-0">Storage Locations</h1>
				<p class="text-muted mb-0">
					The district's location tree — the buildings, rooms, racks and vessels
					product is held in. Any node holds stock, and the depth is the
					district's own organizing method.
				</p>
			</div>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">
			{{ error }}
		</div>

		<div class="card">
			<div class="card-body">
				<h2 class="h6 fw-semibold mb-3">Add a location</h2>
				<form class="row g-2 align-items-end" @submit.prevent="create">
					<div class="col-12 col-md-5 col-lg-4">
						<label class="form-label" for="locationName">Name</label>
						<input
							id="locationName"
							v-model="newName"
							type="text"
							class="form-control"
							placeholder="e.g. Outbuilding 1"
							required
						/>
					</div>
					<div class="col-12 col-md-5 col-lg-4">
						<label class="form-label" for="locationParent">
							Parent <span class="text-muted fw-normal">(optional)</span>
						</label>
						<select
							id="locationParent"
							v-model="newParentID"
							class="form-select"
						>
							<option :value="null">Top level (no parent)</option>
							<option
								v-for="choice in parentChoices"
								:key="choice.id"
								:value="choice.id"
							>
								{{ choice.label }}
							</option>
						</select>
					</div>
					<div class="col-12 col-md-auto">
						<ButtonLoading
							text="Add location"
							icon="bi bi-plus-lg"
							variant="primary"
							:loading="creating"
							:disabled="newName.trim() === ''"
						/>
					</div>
				</form>
			</div>
		</div>

		<div class="card mt-4">
			<div class="card-body">
				<h2 class="h6 fw-semibold mb-3">Location tree</h2>

				<div v-if="loading" class="text-center py-4">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading…</span>
					</div>
				</div>

				<div
					v-else-if="locations.length === 0"
					class="text-center py-4 text-muted"
				>
					No locations yet. Add the district's first one above.
				</div>

				<div v-else class="table-responsive">
					<table class="table table-striped table-hover align-middle">
						<thead class="table-light">
							<tr>
								<th scope="col">
									Location
									<div class="text-muted small fw-normal">
										Rename in place and save
									</div>
								</th>
								<th scope="col">Status</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="node in tree"
								:key="node.id"
								:class="{ 'text-muted': node.retired !== null }"
							>
								<td>
									<span
										class="d-inline-flex align-items-center"
										:style="{ paddingLeft: `${node.depth * 1.5}rem` }"
									>
										<i
											v-if="node.depth > 0"
											class="bi bi-arrow-return-right me-1 text-muted"
										></i>
										<input
											v-model="drafts[node.id]"
											type="text"
											class="form-control form-control-sm"
											style="width: 20rem; max-width: 100%"
											:aria-label="`Name of ${node.name}`"
										/>
									</span>
								</td>
								<td>
									<span
										class="badge rounded-pill"
										:class="
											node.retired === null ? 'bg-success' : 'bg-secondary'
										"
									>
										{{ node.retired === null ? "Active" : "Retired" }}
									</span>
								</td>
								<td class="text-end text-nowrap">
									<ButtonLoading
										v-if="isDirty(node)"
										text="Rename"
										icon="bi bi-check-lg"
										variant="outline-primary"
										size="sm"
										:loading="renamingID === node.id"
										:disabled="!canRename(node)"
										@click="rename(node)"
									/>
									<ButtonLoading
										v-if="node.retired === null"
										text="Retire"
										icon="bi bi-archive"
										variant="outline-secondary"
										size="sm"
										:loading="retiringID === node.id"
										@click="retire(node)"
									/>
									<span v-else class="text-muted small">—</span>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
		</div>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import type { InventoryLocation, LocationCreate } from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

const locations = ref<InventoryLocation[]>([]);
const loading = ref(false);
const error = ref("");
const creating = ref(false);
const renamingID = ref<number | null>(null);
const retiringID = ref<number | null>(null);

// An error is both the notification and the page state: the toast tells the
// operator, and the inline strip keeps the reason on screen after it dismisses.
function fail(title: string, e: any, fallback: string) {
	const message = e?.message || fallback;
	error.value = message;
	showNotification(title, message);
}

const newName = ref("");
const newParentID = ref<number | null>(null);

// A rename is edited in place, so each row keeps its own draft name until the
// row is saved; a row with no draft yet holds the stored name.
const drafts = ref<Record<number, string>>({});

interface TreeNode {
	id: number;
	name: string;
	depth: number;
	retired: string | null;
}

// The location tree, flattened for the table: parents before their children,
// siblings by name, with the depth the name is indented by. Retired nodes stay
// in the tree — past movements and checks still name them — and a node whose
// parent is missing is kept rather than dropped.
function flatten(nodes: InventoryLocation[]): TreeNode[] {
	const byParent = new Map<number | null, InventoryLocation[]>();
	for (const node of nodes) {
		const siblings = byParent.get(node.parent_id) ?? [];
		siblings.push(node);
		byParent.set(node.parent_id, siblings);
	}
	for (const siblings of byParent.values()) {
		siblings.sort((a, b) => a.name.localeCompare(b.name));
	}
	const rows: TreeNode[] = [];
	const visited = new Set<number>();
	const walk = (parent: number | null, depth: number) => {
		for (const node of byParent.get(parent) ?? []) {
			if (visited.has(node.id)) continue;
			visited.add(node.id);
			rows.push({
				id: node.id,
				name: node.name,
				depth,
				retired: node.retired,
			});
			walk(node.id, depth + 1);
		}
	};
	walk(null, 0);
	for (const node of nodes) {
		if (!visited.has(node.id)) {
			rows.push({
				id: node.id,
				name: node.name,
				depth: 0,
				retired: node.retired,
			});
		}
	}
	return rows;
}

const tree = computed(() => flatten(locations.value));

interface LocationChoice {
	id: number;
	label: string;
}

// Parents are the active locations, tree-ordered: a child of a retired node
// could never be reached from a root, so a retired node is not offered.
const parentChoices = computed<LocationChoice[]>(() =>
	flatten(locations.value.filter((node) => node.retired === null)).map(
		(node) => ({
			id: node.id,
			label: `${"\u00a0\u00a0".repeat(node.depth)}${node.name}`,
		}),
	),
);

function isDirty(node: TreeNode): boolean {
	return (drafts.value[node.id] ?? node.name) !== node.name;
}

function canRename(node: TreeNode): boolean {
	return (drafts.value[node.id] ?? "").trim() !== "";
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		// The whole location tree through the generic QUERY endpoint (the legacy
		// GET list's replacement, doc/in-progress/query.md §11 Phase 4). The tree
		// is built in memory from every node, so the walk is what keeps a deep
		// district tree whole rather than cutting it off at a page bound.
		const page = await apiClient.JSONQueryAll<InventoryLocation>(
			"/api/inventory/location",
		);
		const rows = page.items;
		locations.value = rows;
		const next: Record<number, string> = {};
		for (const row of rows) {
			next[row.id] = row.name;
		}
		drafts.value = next;
	} catch (e: any) {
		fail("Failed to load storage locations", e, "Please reload the page.");
	} finally {
		loading.value = false;
	}
}

async function create() {
	const name = newName.value.trim();
	if (name === "") return;
	const body: LocationCreate = { name };
	if (newParentID.value !== null) {
		body.parent_id = newParentID.value;
	}
	creating.value = true;
	try {
		await apiClient.JSONPost<InventoryLocation>(
			"/api/inventory/location",
			body,
		);
		newName.value = "";
		newParentID.value = null;
		await load();
		error.value = "";
		showNotification("Location added", `"${name}" is now part of the tree.`);
	} catch (e: any) {
		fail("Failed to add location", e, "Please try again.");
	} finally {
		creating.value = false;
	}
}

async function rename(node: TreeNode) {
	const name = (drafts.value[node.id] ?? "").trim();
	if (name === "" || name === node.name) return;
	renamingID.value = node.id;
	try {
		await apiClient.JSONPut(`/api/inventory/location/${node.id}`, { name });
		drafts.value[node.id] = name;
		await load();
		error.value = "";
		showNotification("Location renamed", `"${node.name}" is now "${name}".`);
	} catch (e: any) {
		fail("Failed to rename location", e, "Please try again.");
	} finally {
		renamingID.value = null;
	}
}

async function retire(node: TreeNode) {
	if (
		!confirm(
			`Retire "${node.name}"? It stays on past movements and checks, but is ` +
				`no longer offered for new stock.`,
		)
	) {
		return;
	}
	retiringID.value = node.id;
	try {
		await apiClient.JSONDelete(`/api/inventory/location/${node.id}`);
		await load();
		error.value = "";
		showNotification(
			"Location retired",
			`"${node.name}" is no longer offered for new stock.`,
		);
	} catch (e: any) {
		fail("Failed to retire location", e, "Please try again.");
	} finally {
		retiringID.value = null;
	}
}

onMounted(load);
</script>

<style scoped>
.table-responsive {
	overflow-x: auto;
}

th {
	white-space: nowrap;
}
</style>
